import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ShieldCheck, Upload, Download, Search, X, ArrowRight, Info, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// --- helpers -------------------------------------------------------------
const authHeaders = () => {
  const t = localStorage.getItem('safetydesk81_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATO_COLORS = {
  DA_SPOSTARE: 'bg-amber-100 text-amber-900 border-amber-300',
  DA_UNIRE: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300',
  POSSIBILE_DUPLICATO: 'bg-pink-100 text-pink-900 border-pink-300',
  DA_VERIFICARE: 'bg-sky-100 text-sky-900 border-sky-300',
  DA_VERIFICARE_AZIENDA: 'bg-orange-100 text-orange-900 border-orange-400',
  BLOCCATO: 'bg-red-100 text-red-900 border-red-300',
  CF_CONFLITTUALE: 'bg-red-200 text-red-900 border-red-400',
  AZIENDA_DA_NORMALIZZARE: 'bg-indigo-100 text-indigo-900 border-indigo-300',
  NUOVA_AZIENDA_REALE: 'bg-purple-100 text-purple-900 border-purple-300',
  OK: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  OK_INVARIATO: 'bg-teal-100 text-teal-900 border-teal-300',
};
const AFFID_COLORS = {
  ALTA: 'bg-emerald-600 text-white',
  MEDIO_ALTA: 'bg-emerald-500 text-white',
  MEDIA: 'bg-amber-500 text-white',
  BASSA: 'bg-orange-500 text-white',
  DA_VERIFICARE: 'bg-sky-500 text-white',
  BLOCCATA: 'bg-red-600 text-white',
};

const humanNum = (n) => (typeof n === 'number' ? n.toLocaleString('it-IT') : n || 0);

// --- Uploader ------------------------------------------------------------
const Uploader = ({ label, file, onFile, testid }) => (
  <label
    className="flex flex-col gap-1 border border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 hover:bg-slate-100 cursor-pointer"
    data-testid={`${testid}-uploader`}
  >
    <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
    <span className="flex items-center gap-2 text-sm text-slate-800">
      <Upload size={16} className="text-[#023F80]" />
      {file ? file.name : 'Seleziona il file .xlsx'}
    </span>
    <input
      type="file"
      accept=".xlsx"
      className="hidden"
      onChange={(e) => onFile(e.target.files?.[0] || null)}
      data-testid={testid}
    />
  </label>
);

// --- Preview details drawer ---------------------------------------------
const OriginBadge = ({ workerId }) => {
  const src = (workerId || '').startsWith('job81-') ? 'JOB81' : (workerId || '').startsWith('excel-') ? 'EXCEL' : 'ALTRO';
  const cls = src === 'JOB81' ? 'bg-blue-100 text-blue-800 border-blue-300' : src === 'EXCEL' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-700 border-slate-300';
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${cls}`}>{src}</span>;
};

const WorkerCard = ({ w, evidenzeMedico = [], evidenzeFormazione = [], isPrincipal, isCurrent }) => (
  <div className={`p-3 rounded-lg border ${isPrincipal ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-slate-300 bg-white'} ${isCurrent ? 'ring-2 ring-[#023F80]' : ''}`}>
    <div className="flex items-center gap-2 mb-1">
      <OriginBadge workerId={w.worker_id || w.workerId} />
      {isPrincipal && <span className="text-[10px] bg-fuchsia-600 text-white px-1.5 py-0.5 rounded">★ PRINCIPALE</span>}
      {isCurrent && <span className="text-[10px] bg-[#023F80] text-white px-1.5 py-0.5 rounded">Aperto</span>}
    </div>
    <div className="font-semibold text-slate-900">{w.lastName} {w.firstName}</div>
    <div className="text-[11px] text-slate-500 mb-1 break-all">ID: <span className="font-mono">{w.worker_id || w.workerId}</span></div>
    <div className="text-xs space-y-0.5">
      <div><b>CF:</b> {w.fiscalCode || <i className="text-slate-400">—</i>}</div>
      <div><b>Data di nascita:</b> {w.birthDate || <i className="text-slate-400">—</i>}</div>
      <div><b>Azienda attuale:</b> {w.azienda_attuale_nome || <i className="text-slate-400">—</i>}</div>
      <div><b>Record collegati:</b> F:{w.linked_counts?.formazione ?? 0} · V:{w.linked_counts?.visite ?? 0} · D:{w.linked_counts?.dpi ?? 0}</div>
      <div><b>Evidenze:</b> M:{evidenzeMedico.length} · F:{evidenzeFormazione.length}</div>
    </div>
  </div>
);

const RowDetail = ({ token, workerId, onClose, onQuickAssign }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/reconciliation/preview/${token}/row/${workerId}`, { headers: authHeaders() });
        const j = await r.json();
        if (alive) setData(j);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token, workerId]);

  const isDup = data?.row?.duplicate_group_id;
  const partners = data?.partner_details || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="reconciliation-row-detail">
        <DialogHeader>
          <DialogTitle className="text-[#023F80]">
            {isDup ? 'Confronto duplicato' : 'Evidenze lavoratore'}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} />Carico…</div>}
        {!loading && data && (
          <div className="space-y-4">
            {/* Header: sintesi verdict + follow-up */}
            <div className="p-3 bg-slate-50 rounded border border-slate-200 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-900">{data.row.lastName} {data.row.firstName}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded ${STATO_COLORS[data.row.stato] || 'bg-slate-100'}`}>{data.row.stato.replaceAll('_',' ')}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded ${AFFID_COLORS[data.row.affidabilita] || 'bg-slate-500 text-white'}`}>{data.row.affidabilita.replaceAll('_',' ')}</span>
                {data.row.override_applied && <span className="text-[11px] bg-amber-500 text-white px-2 py-0.5 rounded">Override: {data.row.override_applied}</span>}
              </div>
              <div className="text-slate-600 mt-1">Proposta: <b>{data.row.azienda_proposta_nome || '—'}</b> {data.row.azienda_proposta_match_nome_db && data.row.azienda_proposta_match_nome_db !== data.row.azienda_proposta_nome && <span className="text-indigo-700 text-xs">≈ {data.row.azienda_proposta_match_nome_db}</span>}</div>
              {data.row.follow_up && (
                <div className="text-fuchsia-800 text-xs mt-1">Follow-up: {data.row.follow_up.action} → {data.row.follow_up.target_nome || '—'}</div>
              )}
              {data.row.reasons?.length ? <div className="text-slate-600 mt-1 text-xs">Motivi: {data.row.reasons.join(' · ')}</div> : null}
              {data.row.duplicate_reason && (
                <div className="text-fuchsia-900 text-xs mt-1 border-t pt-1"><b>Motivo del match duplicati:</b> {data.row.duplicate_reason}</div>
              )}
            </div>

            {/* Confronto duplicato side-by-side */}
            {isDup && (
              <div data-testid="duplicate-comparison">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Confronto duplicato ({1 + partners.length} record nel gruppo)</div>
                <div className={`grid gap-3 grid-cols-1 ${partners.length >= 1 ? 'md:grid-cols-2' : ''} ${partners.length >= 2 ? 'lg:grid-cols-3' : ''}`}>
                  <WorkerCard
                    w={{ ...data.row, worker_id: data.row.worker_id }}
                    evidenzeMedico={data.evidenze_medico || []}
                    evidenzeFormazione={data.evidenze_formazione || []}
                    isPrincipal={data.row.duplicate_role === 'PRINCIPALE'}
                    isCurrent
                  />
                  {partners.map((p, i) => (
                    <WorkerCard
                      key={p.worker_id || i}
                      w={p}
                      evidenzeMedico={p.evidenze_medico || []}
                      evidenzeFormazione={p.evidenze_formazione || []}
                      isPrincipal={data.row.duplicate_role !== 'PRINCIPALE' /* se non sono io il principale, lo è il partner */}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Evidenze del lavoratore aperto */}
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Evidenze report medico ({data.evidenze_medico?.length || 0})</div>
              <div className="max-h-56 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100"><tr><th className="p-1 text-left">Riga</th><th className="p-1 text-left">Azienda</th><th className="p-1 text-left">Match DB</th><th className="p-1 text-left">Data rif.</th><th className="p-1 text-left">Affidabile</th><th className="p-1 text-right">Azione</th></tr></thead>
                  <tbody>
                    {(data.evidenze_medico || []).map((m,i) => {
                      const match = m.company_match;
                      const hasMatch = match && match.company_id;
                      return (
                        <tr key={i} className="border-t">
                          <td className="p-1">{m.row_num}</td>
                          <td className="p-1 max-w-[240px] truncate" title={m.azienda_raw}>{m.azienda_raw || <i>—</i>}</td>
                          <td className="p-1 max-w-[200px] truncate">
                            {hasMatch ? (
                              <span className="text-emerald-800" title={match.company_name}>≈ {match.company_name}</span>
                            ) : (
                              <span className="text-slate-400 italic">{match?.status === 'ambigua' ? 'ambigua' : match?.status === 'incerta' ? 'incerta' : '—'}</span>
                            )}
                          </td>
                          <td className="p-1">{m.data_ref} <span className="text-slate-400">{m.data_ref_type}</span></td>
                          <td className="p-1">{m.azienda_affidabile ? '✅' : (m.azienda_incerta ? '⚠️' : '—')}</td>
                          <td className="p-1 text-right">
                            {hasMatch && onQuickAssign && (
                              <Button size="sm" className="h-6 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => onQuickAssign({ id: match.company_id, name: match.company_name }, data.row)}
                                data-testid={`recon-quick-ok-${m.row_num}`}>OK → questa</Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(!data.evidenze_medico || data.evidenze_medico.length === 0) && (
                      <tr><td colSpan={6} className="p-2 text-center text-slate-400 italic">Nessuna evidenza medica</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Evidenze formazione ({data.evidenze_formazione?.length || 0})</div>
              <div className="max-h-56 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100"><tr><th className="p-1 text-left">Riga</th><th className="p-1 text-left">Azienda</th><th className="p-1 text-left">Data corso</th><th className="p-1 text-left">CF</th><th className="p-1 text-left">Categoria</th></tr></thead>
                  <tbody>
                    {(data.evidenze_formazione || []).map((f,i) => (
                      <tr key={i} className="border-t"><td className="p-1">{f.row_num}</td><td className="p-1">{f.azienda_raw || <i>—</i>}</td><td className="p-1">{f.data_corso}</td><td className="p-1">{f.cf || '—'}</td><td className="p-1">{f.categoria}</td></tr>
                    ))}
                    {(!data.evidenze_formazione || data.evidenze_formazione.length === 0) && (
                      <tr><td colSpan={5} className="p-2 text-center text-slate-400 italic">Nessuna evidenza formazione</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Confirm dialog for single actions ----------------------------------
const ConfirmActionDialog = ({ open, action, row, onCancel, onConfirm, busy }) => {
  if (!open || !row) return null;
  const isMerge = action === 'merge';
  const title = isMerge ? 'Conferma unione' : 'Conferma spostamento';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="reconciliation-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#023F80]">{title}</DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-2">
          <div><b>Lavoratore:</b> {row.lastName} {row.firstName} <span className="text-xs text-slate-500">({row.worker_id})</span></div>
          {isMerge ? (
            <>
              <div><b>Gruppo:</b> {row.duplicate_group_id}</div>
              <div><b>Motivo:</b> {row.duplicate_reason || '—'}</div>
              <div className="text-fuchsia-800 bg-fuchsia-50 border border-fuchsia-200 rounded p-2 text-xs">
                Le schede duplicate verranno <b>archiviate</b> (reversibile) e tutti i loro record (formazione, visite, DPI, allegati) trasferiti al <b>principale</b>.
              </div>
            </>
          ) : (
            <>
              <div><b>Da:</b> {row.azienda_attuale_nome || '—'}</div>
              <div><b>A:</b> {row.azienda_proposta_match_nome_db || row.azienda_proposta_nome || '—'}</div>
              <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                Verrà aggiornato il <b>companyId</b> del lavoratore e propagato a formazione/visite/DPI/allegati collegati.
              </div>
            </>
          )}
          <div className="text-xs text-slate-500 border-t pt-2 mt-2">
            Prima dell'apply verrà creato un backup automatico di workers, formazione, visite, DPI e allegati (`$out`), quindi l'operazione è annullabile via rollback.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="reconciliation-confirm-cancel">Annulla</Button>
          <Button className="bg-[#023F80] hover:bg-[#022e5f]" onClick={onConfirm} disabled={busy} data-testid="reconciliation-confirm-apply">
            {busy ? <><Loader2 className="animate-spin inline mr-1" size={14} />Applico…</> : (isMerge ? 'Unisci ora' : 'Sposta ora')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Manual company assign dialog ---------------------------------------
const ManualAssignDialog = ({ open, row, preselected, onCancel, onConfirm, busy }) => {
  const [q, setQ] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) { setQ(''); setSelected(null); setCompanies([]); return; }
    // Se arriva con un'azienda preselezionata (dal pulsante "OK → questa"), la usa e apre già confermabile.
    if (preselected) { setSelected(preselected); setCompanies([preselected]); return; }
    const tt = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ status: 'all' });
        if (q) qs.set('search', q);
        const r = await fetch(`${API}/api/companies?${qs.toString()}`, { headers: authHeaders() });
        const j = await r.json();
        setCompanies(Array.isArray(j) ? j.slice(0, 200) : []);
      } catch { setCompanies([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(tt);
  }, [q, open, preselected]);
  if (!open || !row) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl" data-testid="reconciliation-manual-assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#023F80]">Assegna manualmente un'azienda</DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-3">
          <div>
            <div><b>Lavoratore:</b> {row.lastName} {row.firstName} <span className="text-xs text-slate-500">({row.worker_id})</span></div>
            <div className="text-xs text-slate-500">Stato attuale: <b>{row.stato}</b> · Azienda attuale: {row.azienda_attuale_nome || '—'}</div>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda per nome, P.IVA, CF, email…" className="pl-8" data-testid="reconciliation-manual-search" />
          </div>
          <div className="max-h-72 overflow-auto border rounded">
            {loading && <div className="p-3 text-center text-slate-500"><Loader2 className="inline animate-spin mr-1" size={14} />Cerco…</div>}
            {!loading && companies.length === 0 && <div className="p-3 text-center text-slate-500 italic">Nessuna azienda</div>}
            {!loading && companies.map((c) => (
              <button key={c.id} type="button" onClick={() => setSelected(c)}
                className={`w-full text-left px-3 py-2 border-b hover:bg-slate-50 text-sm ${selected?.id === c.id ? 'bg-[#023F80]/10 border-l-4 border-l-[#023F80]' : ''}`}
                data-testid={`reconciliation-manual-company-${c.id}`}>
                <div className="font-semibold text-slate-900">{c.name}</div>
                <div className="text-[11px] text-slate-500">{c.piva || '—'} · {c.address || '—'} {c.status === 'dismesso' && <span className="ml-2 bg-slate-200 text-slate-700 px-1 rounded">dismesso</span>}</div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              Verrà aggiornato il <b>companyId</b> del lavoratore a <b>{selected.name}</b> e propagato a formazione/visite/DPI/allegati collegati. Backup automatico + rollback disponibile.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="reconciliation-manual-cancel">Annulla</Button>
          <Button className="bg-[#023F80] hover:bg-[#022e5f]" onClick={() => selected && onConfirm(selected)} disabled={busy || !selected} data-testid="reconciliation-manual-apply">
            {busy ? <><Loader2 className="animate-spin inline mr-1" size={14} />Applico…</> : 'Assegna azienda'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Main page -----------------------------------------------------------
const ReconciliationPage = () => {
  const { user } = useAuth();
  const [fMedico, setFMedico] = useState(null);
  const [running, setRunning] = useState(false);
  const [token, setToken] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [fStato, setFStato] = useState('all');
  const [fAffid, setFAffid] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fSearch, setFSearch] = useState('');
  const [detailWorker, setDetailWorker] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { action: 'move'|'merge', row }
  const [manualAssign, setManualAssign] = useState(null); // { row }
  const [applyingSingle, setApplyingSingle] = useState(false);
  const [appliedIds, setAppliedIds] = useState({}); // { worker_id | group_id : { opId, action } }

  const isConsultant = user?.role === 'consultant';

  const fetchPage = useCallback(async (t, p, filters) => {
    const qs = new URLSearchParams({ page: String(p), pageSize: String(pageSize) });
    if (filters.stato && filters.stato !== 'all') qs.set('stato', filters.stato);
    if (filters.affid && filters.affid !== 'all') qs.set('affidabilita', filters.affid);
    if (filters.status && filters.status !== 'all') qs.set('worker_status', filters.status);
    if (filters.search) qs.set('search', filters.search);
    const r = await fetch(`${API}/api/admin/reconciliation/preview/${t}?${qs.toString()}`, { headers: authHeaders() });
    const j = await r.json();
    if (r.ok) {
      setRows(j.rows || []);
      setTotal(j.total || 0);
    } else {
      toast.error(j.detail || 'Errore caricamento pagina');
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchPage(token, page, { stato: fStato, affid: fAffid, status: fStatus, search: fSearch });
  }, [token, page, fStato, fAffid, fStatus, fSearch, fetchPage]);

  const generate = async () => {
    if (!fMedico) { toast.error('Carica il Report Medico (.xlsx)'); return; }
    setRunning(true); setToken(null); setSummary(null); setRows([]); setTotal(0); setPage(1);
    try {
      const fd = new FormData();
      fd.append('file_medico', fMedico);
      const r = await fetch(`${API}/api/admin/reconciliation/preview`, { method: 'POST', headers: authHeaders(), body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Errore preview');
      setToken(j.token);
      setSummary(j.summary);
      setTotal(j.total_rows || 0);
      toast.success(`Preview generata: ${j.total_rows} lavoratori analizzati`);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setRunning(false); }
  };

  const totals = summary?.totals || { by_stato: {}, by_affidabilita: {}, by_worker_status: {} };

  const doExport = async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/admin/reconciliation/preview/${token}/export.xlsx`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Errore export');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `preview_riconciliazione_${token.slice(0,12)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  const applySingle = async () => {
    if (!confirmAction || !token) return;
    const { action, row } = confirmAction;
    setApplyingSingle(true);
    try {
      const body = { previewToken: token, action, confirm: true };
      if (action === 'move') body.worker_id = row.worker_id;
      else body.group_id = row.duplicate_group_id;
      const r = await fetch(`${API}/api/admin/reconciliation/apply/single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Errore apply');
      const key = action === 'move' ? row.worker_id : row.duplicate_group_id;
      setAppliedIds((prev) => ({ ...prev, [key]: { opId: j.opId, action } }));

      // Aggiorna lo stato locale: rimuovi la/le riga/righe applicate dalla tabella
      // e decrementa i contatori del summary. Il preview persistito in DB resta immutato:
      // la fotografia serve solo come riferimento, ma la UI riflette quello che è già stato eseguito.
      let removedCount = 1;
      let removedStato = row.stato;
      if (action === 'merge' && row.duplicate_group_id) {
        setRows((prev) => {
          const filtered = prev.filter((x) => x.duplicate_group_id !== row.duplicate_group_id);
          removedCount = prev.length - filtered.length;
          return filtered;
        });
      } else {
        setRows((prev) => prev.filter((x) => x.worker_id !== row.worker_id));
      }
      setTotal((prev) => Math.max(0, prev - removedCount));
      setSummary((prev) => {
        if (!prev) return prev;
        const s = { ...prev };
        const totals = { ...(s.totals || {}) };
        const by_stato = { ...(totals.by_stato || {}) };
        by_stato[removedStato] = Math.max(0, (by_stato[removedStato] || 0) - removedCount);
        totals.by_stato = by_stato;
        // Aggiorna anche contatori duplicati se merge
        if (action === 'merge' && s.duplicati) {
          const dup = { ...s.duplicati };
          if (row.stato === 'DA_UNIRE') dup.da_unire = Math.max(0, (dup.da_unire || 0) - 1);
          if (row.stato === 'POSSIBILE_DUPLICATO') dup.possibili = Math.max(0, (dup.possibili || 0) - 1);
          dup.record_in_gruppi = Math.max(0, (dup.record_in_gruppi || 0) - removedCount);
          s.duplicati = dup;
        }
        s.totals = totals;
        return s;
      });

      toast.success(action === 'move' ? `Spostato · opId ${j.opId}` : `Unito · opId ${j.opId}`);
      setConfirmAction(null);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setApplyingSingle(false);
    }
  };

  const applyManualAssign = async (company) => {
    if (!manualAssign || !token || !company) return;
    const row = manualAssign.row;
    setApplyingSingle(true);
    try {
      const body = { previewToken: token, action: 'move', confirm: true, worker_id: row.worker_id, target_company_id: company.id };
      const r = await fetch(`${API}/api/admin/reconciliation/apply/single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Errore apply');
      setAppliedIds((prev) => ({ ...prev, [row.worker_id]: { opId: j.opId, action: 'manual_move', target: company.name } }));
      setRows((prev) => prev.filter((x) => x.worker_id !== row.worker_id));
      setTotal((prev) => Math.max(0, prev - 1));
      setSummary((prev) => {
        if (!prev) return prev;
        const s = { ...prev };
        const totals = { ...(s.totals || {}) };
        const by_stato = { ...(totals.by_stato || {}) };
        by_stato[row.stato] = Math.max(0, (by_stato[row.stato] || 0) - 1);
        totals.by_stato = by_stato;
        s.totals = totals;
        return s;
      });
      toast.success(`Assegnata a ${company.name} · opId ${j.opId}`);
      setManualAssign(null);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setApplyingSingle(false);
    }
  };

  if (!isConsultant) {
    return <div className="p-8 text-slate-600">Sezione riservata ai consulenti.</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px]" data-testid="reconciliation-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#023F80]">Riconciliazione Lavoratore ↔ Azienda</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            Anteprima in sola lettura: confronta i lavoratori attualmente in produzione con l'associazione aziendale ricavata dal Report Medico (fonte unica). Il file Formazione non viene più utilizzato per proporre o modificare l'azienda.
          </p>
          <a href="/riconciliazione/duplicati" data-testid="link-duplicates-recon" className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-gradient-to-r from-[#023F80] to-[#022F60] hover:from-[#022F60] hover:to-[#01214a] text-white text-sm font-bold shadow">
            👥 Riconciliazione lavoratori duplicati →
          </a>
        </div>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800 text-xs">
          <ShieldCheck size={16} /> Sola lettura — nessuna modifica al database
        </div>
      </header>

      {/* Upload row */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <Uploader label="Report Medico (.xlsx)" file={fMedico} onFile={setFMedico} testid="reconciliation-file-medico" />
        <div className="flex gap-2 md:col-span-2">
          <Button className="bg-[#023F80] hover:bg-[#022e5f]" onClick={generate} disabled={running || !fMedico} data-testid="reconciliation-generate-btn">
            {running ? <><Loader2 size={16} className="animate-spin mr-2" />Analisi in corso…</> : 'Genera anteprima'}
          </Button>
          {token && (
            <Button variant="outline" onClick={doExport} data-testid="reconciliation-export-btn">
              <Download size={16} className="mr-2" /> Esporta XLSX
            </Button>
          )}
        </div>
      </section>

      {/* Summary */}
      {summary && (
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-3">
          {['DA_SPOSTARE','DA_UNIRE','POSSIBILE_DUPLICATO','DA_VERIFICARE_AZIENDA','DA_VERIFICARE','BLOCCATO','CF_CONFLITTUALE','AZIENDA_DA_NORMALIZZARE','NUOVA_AZIENDA_REALE','OK_INVARIATO','OK'].map((k) => (
            <div key={k} className={`p-3 rounded-lg border ${STATO_COLORS[k] || 'bg-slate-50 border-slate-200'}`} data-testid={`summary-${k}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80">{k.replaceAll('_',' ')}</div>
              <div className="text-2xl font-bold mt-1">{humanNum(totals.by_stato?.[k] || 0)}</div>
            </div>
          ))}
        </section>
      )}

      {summary && (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="p-3 rounded-lg border bg-white shadow-sm">
            <div className="text-xs font-semibold text-slate-700 mb-1">Stato lavoratore</div>
            {Object.entries(totals.by_worker_status || {}).map(([k,v]) => (
              <div key={k} className="flex justify-between text-sm"><span>{k}</span><span className="font-mono">{humanNum(v)}</span></div>
            ))}
          </div>
          <div className="p-3 rounded-lg border bg-white shadow-sm">
            <div className="text-xs font-semibold text-slate-700 mb-1">Affidabilità</div>
            {Object.entries(totals.by_affidabilita || {}).map(([k,v]) => (
              <div key={k} className="flex justify-between text-sm"><span>{k.replaceAll('_',' ')}</span><span className="font-mono">{humanNum(v)}</span></div>
            ))}
          </div>
          <div className="p-3 rounded-lg border bg-fuchsia-50 border-fuchsia-200 shadow-sm">
            <div className="text-xs font-semibold text-fuchsia-900 mb-1">Duplicati (workers)</div>
            {Object.entries(summary.duplicati || {}).map(([k,v]) => (
              <div key={k} className="flex justify-between text-sm"><span>{k.replaceAll('_',' ')}</span><span className="font-mono">{humanNum(v)}</span></div>
            ))}
          </div>
          <div className="p-3 rounded-lg border bg-white shadow-sm">
            <div className="text-xs font-semibold text-slate-700 mb-1">Privati — universi disgiunti</div>
            <div className="flex justify-between text-sm border-b pb-1 mb-1"><span className="font-semibold">Lavoratori unici in Privati</span><span className="font-mono font-semibold">{humanNum(summary.privati?.lavoratori_unici_in_privati)}</span></div>
            <div className="flex justify-between text-xs"><span>&nbsp;&nbsp;con proposta</span><span className="font-mono">{humanNum(summary.privati?.con_proposta_aziendale)}</span></div>
            <div className="flex justify-between text-xs"><span>&nbsp;&nbsp;senza proposta</span><span className="font-mono">{humanNum(summary.privati?.senza_proposta_aziendale)}</span></div>
            <div className="text-[10px] font-semibold text-slate-500 uppercase mt-2">Dettaglio "con proposta"</div>
            {Object.entries(summary.privati?.dettaglio_stato_con_proposta || {}).map(([k,v]) => (
              k === 'check_somma' ? null : (
                <div key={k} className="flex justify-between text-xs"><span>&nbsp;&nbsp;{k.replaceAll('_',' ')}</span><span className="font-mono">{humanNum(v)}</span></div>
              )
            ))}
            <div className="text-[10px] font-semibold text-slate-500 uppercase mt-2">In gruppo duplicati (sottoinsieme)</div>
            <div className="flex justify-between text-xs"><span>&nbsp;&nbsp;lavoratori unici</span><span className="font-mono">{humanNum(summary.privati?.in_gruppo_duplicati?.lavoratori_unici)}</span></div>
            <div className="flex justify-between text-xs"><span>&nbsp;&nbsp;gruppi coinvolti</span><span className="font-mono">{humanNum(summary.privati?.in_gruppo_duplicati?.gruppi_coinvolti)}</span></div>
            <div className="flex justify-between text-xs"><span>&nbsp;&nbsp;con proposta (overlap)</span><span className="font-mono">{humanNum(summary.privati?.in_gruppo_duplicati?.sovrapposti_con_con_proposta)}</span></div>
          </div>
          <div className="p-3 rounded-lg border bg-white shadow-sm">
            <div className="text-xs font-semibold text-slate-700 mb-1">Aziende con più cambi (top 5)</div>
            {(summary.top_aziende_con_cambi || []).slice(0,5).map((x,i) => (
              <div key={i} className="flex justify-between text-sm gap-2"><span className="truncate" title={x.companyName}>{x.companyName}</span><span className="font-mono">{humanNum(x.count)}</span></div>
            ))}
          </div>
        </section>
      )}

      {/* Overrides applicati */}
      {summary?.overrides_applied?.length ? (
        <section className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm" data-testid="overrides-applied">
          <div className="font-semibold text-amber-900 mb-1">Override utente applicati ({summary.overrides_applied.filter(o=>o.applied).length}/{summary.overrides_applied.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.overrides_applied.map((ov,i)=>(
              <div key={i} className={`p-2 rounded border ${ov.applied ? 'border-amber-400 bg-white':'border-slate-300 bg-slate-50 text-slate-500'}`}>
                <div className="font-semibold">{(ov.tokens||[]).join(' ')} <span className="text-[10px] uppercase ml-1">{ov.kind}</span></div>
                <div className="text-xs">{ov.note}</div>
                <div className="text-[11px] text-slate-500 mt-1">{ov.applied ? `${ov.matched_worker_ids.length} record aggiornati` : 'Nessun record trovato in DB'}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Filters + Table */}
      {token && (
        <section className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded border border-slate-200">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input placeholder="Cerca cognome/CF/azienda…" className="pl-7 h-8 w-72"
                value={fSearch} onChange={(e) => { setPage(1); setFSearch(e.target.value); }}
                data-testid="reconciliation-search" />
            </div>
            <Select value={fStato} onValueChange={(v) => { setPage(1); setFStato(v); }}>
              <SelectTrigger className="h-8 w-56" data-testid="reconciliation-filter-stato"><SelectValue placeholder="Stato riga" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="DA_SPOSTARE">DA_SPOSTARE</SelectItem>
                <SelectItem value="DA_UNIRE">DA_UNIRE</SelectItem>
                <SelectItem value="POSSIBILE_DUPLICATO">POSSIBILE_DUPLICATO</SelectItem>
                <SelectItem value="DA_VERIFICARE_AZIENDA">DA_VERIFICARE_AZIENDA</SelectItem>
                <SelectItem value="DA_VERIFICARE">DA_VERIFICARE</SelectItem>
                <SelectItem value="BLOCCATO">BLOCCATO</SelectItem>
                <SelectItem value="CF_CONFLITTUALE">CF_CONFLITTUALE</SelectItem>
                <SelectItem value="AZIENDA_DA_NORMALIZZARE">AZIENDA_DA_NORMALIZZARE</SelectItem>
                <SelectItem value="NUOVA_AZIENDA_REALE">NUOVA_AZIENDA_REALE</SelectItem>
                <SelectItem value="OK_INVARIATO">OK_INVARIATO</SelectItem>
                <SelectItem value="OK">OK</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fAffid} onValueChange={(v) => { setPage(1); setFAffid(v); }}>
              <SelectTrigger className="h-8 w-56" data-testid="reconciliation-filter-affid"><SelectValue placeholder="Affidabilità" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le affidabilità</SelectItem>
                <SelectItem value="ALTA">ALTA</SelectItem>
                <SelectItem value="MEDIO_ALTA">MEDIO_ALTA</SelectItem>
                <SelectItem value="MEDIA">MEDIA</SelectItem>
                <SelectItem value="BASSA">BASSA</SelectItem>
                <SelectItem value="DA_VERIFICARE">DA_VERIFICARE</SelectItem>
                <SelectItem value="BLOCCATA">BLOCCATA</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={(v) => { setPage(1); setFStatus(v); }}>
              <SelectTrigger className="h-8 w-40" data-testid="reconciliation-filter-status"><SelectValue placeholder="Stato lavoratore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="attivo">Attivi</SelectItem>
                <SelectItem value="dismesso">Cessati</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-slate-500">Risultati: <b>{humanNum(total)}</b></div>
          </div>

          <div className="overflow-auto border rounded-lg bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
                <tr>
                  <th className="p-2 text-left">Lavoratore</th>
                  <th className="p-2 text-left">Stato lav.</th>
                  <th className="p-2 text-left">Azienda attuale</th>
                  <th className="p-2 text-center"></th>
                  <th className="p-2 text-left">Azienda proposta</th>
                  <th className="p-2 text-left">Fonte / Data</th>
                  <th className="p-2 text-left">Stato riga</th>
                  <th className="p-2 text-left">Affidabilità</th>
                  <th className="p-2 text-left">Match</th>
                  <th className="p-2 text-left">Note</th>
                  <th className="p-2 text-right">Evidenze</th>
                  <th className="p-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const canMove = (r.stato === 'DA_SPOSTARE' || r.stato === 'AZIENDA_DA_NORMALIZZARE') && r.azienda_proposta_id;
                  const canMerge = (r.stato === 'DA_UNIRE' || r.stato === 'POSSIBILE_DUPLICATO') && r.duplicate_group_id && r.duplicate_role === 'PRINCIPALE';
                  const appliedMove = appliedIds[r.worker_id];
                  const appliedMerge = r.duplicate_group_id ? appliedIds[r.duplicate_group_id] : null;
                  return (
                  <tr key={r.worker_id} className="border-t hover:bg-slate-50" data-testid={`reconciliation-row-${r.worker_id}`}>
                    <td className="p-2 cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>
                      <div className="font-semibold text-slate-900 flex items-center gap-1">
                        {r.lastName} {r.firstName}
                        {r.duplicate_group_id && (
                          <span className={`text-[9px] px-1 py-0.5 rounded ${r.duplicate_role === 'PRINCIPALE' ? 'bg-fuchsia-600 text-white' : 'bg-fuchsia-200 text-fuchsia-900'}`}>
                            {r.duplicate_role === 'PRINCIPALE' ? '★ PRINC.' : r.duplicate_role === 'DUPLICATO' ? 'DUP' : 'POSS'}
                          </span>
                        )}
                        {r.override_applied && <span className="text-[9px] bg-amber-500 text-white px-1 py-0.5 rounded">OVERRIDE</span>}
                      </div>
                      <div className="text-[11px] text-slate-500">{r.fiscalCode || '—'} · {r.birthDate || '—'}</div>
                      {r.follow_up && (
                        <div className="text-[10px] text-fuchsia-800 mt-0.5">↳ {r.follow_up.action}{r.follow_up.target_nome ? ` → ${r.follow_up.target_nome}` : ''}</div>
                      )}
                    </td>
                    <td className="p-2 text-xs cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>
                      <Badge variant="outline" className={r.worker_status === 'dismesso' ? 'border-slate-400 text-slate-600' : 'border-emerald-500 text-emerald-700'}>{r.worker_status}</Badge>
                    </td>
                    <td className="p-2 max-w-[220px] truncate cursor-pointer" title={r.azienda_attuale_nome} onClick={() => setDetailWorker(r.worker_id)}>{r.azienda_attuale_nome || <i className="text-slate-400">—</i>}</td>
                    <td className="p-2 text-slate-400"><ArrowRight size={14} /></td>
                    <td className="p-2 max-w-[220px] truncate cursor-pointer" title={r.azienda_proposta_nome || ''} onClick={() => setDetailWorker(r.worker_id)}>
                      {r.azienda_proposta_nome ? <>
                        <div>{r.azienda_proposta_nome}</div>
                        {r.azienda_proposta_match_nome_db && r.match_quality !== 'exact' && (
                          <div className="text-[11px] text-indigo-700">≈ {r.azienda_proposta_match_nome_db}</div>
                        )}
                      </> : <i className="text-slate-400">—</i>}
                    </td>
                    <td className="p-2 text-xs cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>
                      {r.fonte === 'medico' && <span className="text-emerald-700 font-semibold">medico</span>}
                      {r.fonte === 'formazione' && <span className="text-amber-700 font-semibold">formazione</span>}
                      {r.fonte === 'nessuna' && <span className="text-slate-500">—</span>}
                      {r.data_riferimento && <div className="text-slate-500">{r.data_riferimento}</div>}
                    </td>
                    <td className="p-2 cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}><Badge className={STATO_COLORS[r.stato] || 'bg-slate-100 text-slate-800'}>{r.stato.replaceAll('_',' ')}</Badge></td>
                    <td className="p-2 cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}><Badge className={AFFID_COLORS[r.affidabilita] || 'bg-slate-500 text-white'}>{r.affidabilita.replaceAll('_',' ')}</Badge></td>
                    <td className="p-2 text-xs cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>
                      {r.match_quality === 'exact' && <span className="text-emerald-700">esatto</span>}
                      {r.match_quality === 'qualifying_equal' && <span className="text-indigo-700">parole ok</span>}
                      {r.match_quality === 'qualifying_disamb' && <span className="text-indigo-800">disambig.</span>}
                      {r.match_quality === 'ambigua' && <span className="text-red-700">ambigua</span>}
                      {r.match_quality === 'override' && <span className="text-amber-700">override</span>}
                      {r.match_quality === 'none' && <span className="text-slate-500">nessuno</span>}
                      {!r.match_quality && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2 text-[11px] text-slate-600 max-w-[300px] cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>
                      {(r.reasons || []).join(' · ')}
                    </td>
                    <td className="p-2 text-right text-xs text-slate-500 whitespace-nowrap cursor-pointer" onClick={() => setDetailWorker(r.worker_id)}>M:{r.evidenze_medico_count} · F:{r.evidenze_formazione_count}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {appliedMove ? (
                        <span className="text-[11px] bg-emerald-600 text-white px-2 py-1 rounded" title={`opId ${appliedMove.opId}`}>✓ {appliedMove.action === 'manual_move' ? 'Assegnato' : 'Spostato'}</span>
                      ) : appliedMerge ? (
                        <span className="text-[11px] bg-emerald-600 text-white px-2 py-1 rounded" title={`opId ${appliedMerge.opId}`}>✓ Unito</span>
                      ) : (
                        <div className="flex flex-col gap-1 items-end">
                          {canMove && (
                            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs px-2"
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ action: 'move', row: r }); }}
                              data-testid={`reconciliation-move-btn-${r.worker_id}`}>Sposta ✓</Button>
                          )}
                          {canMerge && (
                            <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-7 text-xs px-2"
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ action: 'merge', row: r }); }}
                              data-testid={`reconciliation-merge-btn-${r.worker_id}`}>
                              {r.stato === 'POSSIBILE_DUPLICATO' ? 'Conferma unione ✓' : 'Unisci ✓'}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2 border-[#023F80] text-[#023F80] hover:bg-[#023F80]/5"
                            onClick={(e) => { e.stopPropagation(); setManualAssign({ row: r }); }}
                            data-testid={`reconciliation-manual-btn-${r.worker_id}`}>Assegna azienda ✎</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );})}
                {rows.length === 0 && (
                  <tr><td colSpan={12} className="p-8 text-center text-slate-500">Nessuna riga per i filtri correnti</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center text-sm">
            <div className="text-slate-500">Pagina {page} di {Math.max(1, Math.ceil(total / pageSize))}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="reconciliation-prev-page">Precedente</Button>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(page + 1)} data-testid="reconciliation-next-page">Successiva</Button>
            </div>
          </div>
        </section>
      )}

      {detailWorker && token && (
        <RowDetail token={token} workerId={detailWorker} onClose={() => setDetailWorker(null)}
          onQuickAssign={(company, row) => { setDetailWorker(null); setManualAssign({ row, preselected: company }); }} />
      )}
      <ConfirmActionDialog
        open={!!confirmAction}
        action={confirmAction?.action}
        row={confirmAction?.row}
        busy={applyingSingle}
        onCancel={() => setConfirmAction(null)}
        onConfirm={applySingle}
      />
      <ManualAssignDialog
        open={!!manualAssign}
        row={manualAssign?.row}
        preselected={manualAssign?.preselected}
        busy={applyingSingle}
        onCancel={() => setManualAssign(null)}
        onConfirm={applyManualAssign}
      />
    </div>
  );
};

export default ReconciliationPage;
